/**
 * Chat API functions
 */
import { ENDPOINTS } from './endpoints';
import { getRequestBackendDataV2, postRequestBackendDataV2, getBaseApiUrl } from './core';
import logger from '../utils/logger';

export async function postCreateChatV2(auth, callback, chatName, provider, category, ticker) {
    if (!chatName || !provider || !category || !ticker) {
        callback({ error: "No instrument selected" });
        return;
    }
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.CHAT.CREATE, { 
        chatName, 
        provider, 
        category, 
        ticker 
    });
}

export async function postSendMessageV2(auth, callback, chatId, messages, base64_image, metadata, model) {
    const data = { content: messages };
    if (base64_image) {
        data.base64_image = base64_image;
    }
    if (metadata) {
        data.metadata = metadata;
    }
    if (model) {
        data.model = model;
    }
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.CHAT.MESSAGE(chatId), data);
}

export const deleteChatV2 = async (auth, callback, chatId) => {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.CHAT.DELETE(chatId), {});
};

export async function getChatMessagesV2(auth, callback, chatId) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.CHAT.MESSAGES(chatId));
}

export async function getUserChatsV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.CHAT.LIST);
}

export const clearChatMessagesV2 = async (auth, callback, chatId) => {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.CHAT.CLEAR(chatId), {});
};

/**
 * Stream chat messages using Server-Sent Events (SSE)
 * @param {Object} auth - Authentication object
 * @param {Function} onMessage - Callback for message events
 * @param {Function} onError - Callback for error events
 * @param {Function} onComplete - Callback for complete events
 * @param {Function} onLimit - Callback for limit events
 * @param {Function} onProcessing - Callback for processing events
 * @param {string} chatId - Chat ID
 * @param {string} message - Message content
 * @param {string} base64_image - Base64 encoded image (optional)
 * @param {Object} metadata - Additional metadata (optional)
 * @param {string} model - Model ID (optional)
 * @param {Function} onThinkingStart - Callback for thinking start events (optional)
 * @param {Function} onThinkingDelta - Callback for thinking delta events (optional)
 * @param {Function} onThinkingEnd - Callback for thinking end events (optional)
 * @param {Function} onToolStart - Callback for tool start events (optional)
 * @param {Function} onToolEnd - Callback for tool end events (optional)
 * @returns {Function} - Function to close the connection
 */
export function streamChatMessageV2(
    auth,
    onMessage,
    onError,
    onComplete,
    onLimit,
    onProcessing,
    chatId,
    message,
    base64_image = null,
    metadata = null,
    model = null,
    onThinkingStart = null,
    onThinkingDelta = null,
    onThinkingEnd = null,
    onToolStart = null,
    onToolEnd = null
) {
    const controller = new AbortController();

    // Check if auth and currentUser exist
    if (!auth?.currentUser) {
        onError({ error: "Not authenticated" });
        return () => {};
    }

    (async () => {
    try {
        const idToken = await auth.currentUser.getIdToken(false);
        const url = getBaseApiUrl() + ENDPOINTS.CHAT.STREAM_MESSAGE(chatId);

        const data = { content: message };
        if (base64_image) {
            data.base64_image = base64_image;
        }
        if (metadata) {
            data.metadata = metadata;
        }
        if (model) {
            data.model = model;
        }

        // Create a POST request with the data
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify(data),
            signal: controller.signal,
        });

        // Check if the response is ok
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the response as a ReadableStream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Process the stream
        const processStream = async () => {
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    
                    if (done) {
                        break;
                    }
                    
                    // Decode the chunk and add it to the buffer
                    buffer += decoder.decode(value, { stream: true });
                    
                    // Process complete SSE messages in the buffer
                    const lines = buffer.split('\n\n');
                    buffer = lines.pop() || ''; // Keep the last incomplete line in the buffer
                    
                    for (const line of lines) {
                        if (line.trim() === '') continue;
                        
                        // Parse SSE format
                        const eventLines = line.split('\n');
                        let eventName = 'message';
                        let eventData = '';
                        
                        for (const eventLine of eventLines) {
                            if (eventLine.startsWith('event:')) {
                                eventName = eventLine.substring(6).trim();
                            } else if (eventLine.startsWith('data:')) {
                                eventData = eventLine.substring(5).trim();
                            }
                        }
                        
                        if (eventData) {
                            try {
                                const parsedData = JSON.parse(eventData);
                                
                                // Call the appropriate callback based on the event name
                                switch (eventName) {
                                    case 'message':
                                        onMessage(parsedData);
                                        break;
                                    case 'error':
                                        onError(parsedData);
                                        break;
                                    case 'complete':
                                        onComplete(parsedData);
                                        break;
                                    case 'limit':
                                        onLimit(parsedData);
                                        break;
                                    case 'processing':
                                        onProcessing(parsedData);
                                        break;
                                    case 'thinking_start':
                                        if (onThinkingStart) onThinkingStart(parsedData);
                                        break;
                                    case 'thinking_delta':
                                        if (onThinkingDelta) onThinkingDelta(parsedData);
                                        break;
                                    case 'thinking_end':
                                        if (onThinkingEnd) onThinkingEnd(parsedData);
                                        break;
                                    case 'tool_start':
                                        if (onToolStart) onToolStart(parsedData);
                                        break;
                                    case 'tool_end':
                                        if (onToolEnd) onToolEnd(parsedData);
                                        break;
                                    default:
                                        logger.log(`Unknown event: ${eventName}`, parsedData);
                                }
                            } catch (error) {
                                logger.error(`Error parsing event data: ${error.message}`, eventData);
                            }
                        }
                    }
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    logger.error("Error processing stream:", error);
                    onError({ error: "Error processing stream" });
                }
            }
        };

        // Start processing the stream
        processStream();
    } catch (error) {
        if (error.name !== 'AbortError') {
            logger.error("Error setting up streaming connection:", error);
            onError({ error: "Failed to establish streaming connection" });
        }
    }
    })();

    return () => controller.abort();
}

